import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { firmaAPI, kurumAPI, ilceAPI } from '../api';
import FirmaModal from '../components/FirmaModal';
import ImportModal from '../components/ImportModal';
import toast from 'react-hot-toast';
import {
  Plus, Search, Download, Upload, Trash2, Filter,
  ChevronUp, ChevronDown, Mail, Phone, StickyNote,
  RefreshCw, X, Eye, Edit2
} from 'lucide-react';

const PAGE_SIZES = [25, 50, 100];

export default function FirmalarPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [firmalar, setFirmalar] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);

  const [q, setQ] = useState('');
  const [ilceFilter, setIlceFilter] = useState('');
  const [kurumFilter, setKurumFilter] = useState('');
  const [mailEksik, setMailEksik] = useState(searchParams.get('mail_eksik') === 'true');
  const [telEksik, setTelEksik] = useState(searchParams.get('tel_eksik') === 'true');
  const [notuVar, setNotuVar] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [sortBy, setSortBy] = useState('sira');
  const [sortDir, setSortDir] = useState('asc');

  const [kurumlar, setKurumlar] = useState([]);
  const [ilceler, setIlceler] = useState([]);

  const [selectedIds, setSelectedIds] = useState(new Set());
  const [editFirma, setEditFirma] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const searchTimeout = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await firmaAPI.list({
        q: q || undefined,
        ilce: ilceFilter || undefined,
        kurum_id: kurumFilter || undefined,
        mail_eksik: mailEksik || undefined,
        tel_eksik: telEksik || undefined,
        notu_var: notuVar || undefined,
        page, page_size: pageSize,
        sort_by: sortBy, sort_dir: sortDir,
      });
      setFirmalar(res.data.items);
      setTotal(res.data.total);
      setTotalPages(res.data.total_pages);
    } catch {
      toast.error('Firmalar yüklenemedi');
    } finally {
      setLoading(false);
    }
  }, [q, ilceFilter, kurumFilter, mailEksik, telEksik, notuVar, page, pageSize, sortBy, sortDir]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    kurumAPI.list().then((r) => setKurumlar(r.data));
    ilceAPI.list().then((r) => setIlceler(r.data));
  }, []);

  const handleSearch = (val) => {
    clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => {
      setQ(val);
      setPage(1);
    }, 300);
  };

  const toggleSort = (col) => {
    if (sortBy === col) setSortDir((d) => d === 'asc' ? 'desc' : 'asc');
    else { setSortBy(col); setSortDir('asc'); }
  };

  const SortIcon = ({ col }) => {
    if (sortBy !== col) return <ChevronUp size={12} className="text-navy-300" />;
    return sortDir === 'asc'
      ? <ChevronUp size={12} className="text-navy-600" />
      : <ChevronDown size={12} className="text-navy-600" />;
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (selectedIds.size === firmalar.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(firmalar.map((f) => f.id)));
  };

  const handleDelete = async (id) => {
    try {
      await firmaAPI.delete(id);
      toast.success('Firma silindi');
      setDeleteConfirm(null);
      load();
    } catch { toast.error('Silme başarısız'); }
  };

  const handleBulkDelete = async () => {
    try {
      await firmaAPI.bulkDelete([...selectedIds]);
      toast.success(`${selectedIds.size} firma silindi`);
      setSelectedIds(new Set());
      load();
    } catch { toast.error('Silme başarısız'); }
  };

  const handleExportCSV = async (all = false) => {
    try {
      const res = await firmaAPI.exportCSV({
        q: q || undefined, ilce: ilceFilter || undefined,
        kurum_id: kurumFilter || undefined, all,
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url; a.download = 'firmalar.csv'; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('Dışa aktarım başarısız'); }
  };

  const resetFilters = () => {
    setQ(''); setIlceFilter(''); setKurumFilter('');
    setMailEksik(false); setTelEksik(false); setNotuVar(false);
    setPage(1);
  };

  const hasFilter = q || ilceFilter || kurumFilter || mailEksik || telEksik || notuVar;

  return (
    <div className="space-y-4 animate-fade-in">
      {/* Başlık */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-display text-navy-800">Firmalar</h1>
          <p className="text-navy-400 text-sm mt-0.5">
            Toplam <strong>{total.toLocaleString('tr-TR')}</strong> firma
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button className="btn-secondary" onClick={() => setShowImport(true)}>
            <Upload size={16} /> İçe Aktar
          </button>
          <div className="relative group">
            <button className="btn-secondary">
              <Download size={16} /> Dışa Aktar
            </button>
            <div className="absolute right-0 top-full mt-1 bg-white rounded-xl shadow-card-hover border border-navy-100 z-10 hidden group-hover:block min-w-[170px]">
              <button className="w-full text-left px-4 py-2.5 text-sm text-navy-700 hover:bg-navy-50 rounded-t-xl"
                onClick={() => handleExportCSV(false)}>CSV — Filtrelenmiş</button>
              <button className="w-full text-left px-4 py-2.5 text-sm text-navy-700 hover:bg-navy-50 rounded-b-xl"
                onClick={() => handleExportCSV(true)}>CSV — Tümü</button>
            </div>
          </div>
          <button className="btn-primary" onClick={() => { setEditFirma(null); setShowModal(true); }}>
            <Plus size={16} /> Yeni Firma
          </button>
        </div>
      </div>

      {/* Filtreler */}
      <div className="card p-4">
        <div className="flex flex-wrap gap-3 items-center">
          <div className="relative flex-1 min-w-[200px]">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-navy-300" />
            <input
              className="input pl-8"
              placeholder="Firma adı, mail, telefon…"
              defaultValue={q}
              onChange={(e) => handleSearch(e.target.value)}
            />
          </div>

          <select className="select w-auto min-w-[140px]" value={ilceFilter}
            onChange={(e) => { setIlceFilter(e.target.value); setPage(1); }}>
            <option value="">Tüm İlçe/OSB</option>
            {ilceler.map((i) => <option key={i.id} value={i.ad}>{i.ad}</option>)}
          </select>

          <select className="select w-auto min-w-[140px]" value={kurumFilter}
            onChange={(e) => { setKurumFilter(e.target.value); setPage(1); }}>
            <option value="">Tüm Kurumlar</option>
            {kurumlar.map((k) => <option key={k.id} value={k.id}>{k.ad}</option>)}
          </select>

          {/* Hızlı filtreler */}
          <div className="flex gap-2 flex-wrap">
            {[
              { label: 'Mail Eksik', icon: Mail, val: mailEksik, set: setMailEksik },
              { label: 'Tel Eksik', icon: Phone, val: telEksik, set: setTelEksik },
              { label: 'Notu Var', icon: StickyNote, val: notuVar, set: setNotuVar },
            ].map(({ label, icon: Icon, val, set: setter }) => (
              <button
                key={label}
                onClick={() => { setter(!val); setPage(1); }}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all
                  ${val ? 'bg-navy-600 text-white' : 'bg-navy-100 text-navy-600 hover:bg-navy-200'}`}
              >
                <Icon size={13} />{label}
              </button>
            ))}
          </div>

          {hasFilter && (
            <button className="btn-ghost text-rose-500 hover:text-rose-700" onClick={resetFilters}>
              <RefreshCw size={14} /> Sıfırla
            </button>
          )}

          <select className="select w-auto ml-auto"
            value={pageSize} onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}>
            {PAGE_SIZES.map((s) => <option key={s} value={s}>{s} / sayfa</option>)}
          </select>
        </div>
      </div>

      {/* Toplu işlemler */}
      {selectedIds.size > 0 && (
        <div className="flex items-center gap-3 bg-navy-600 text-white px-4 py-2.5 rounded-xl animate-slide-up">
          <span className="text-sm font-medium">{selectedIds.size} firma seçildi</span>
          <button
            className="ml-auto flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-red-500 hover:bg-red-600 text-sm font-medium transition-colors"
            onClick={handleBulkDelete}
          >
            <Trash2 size={14} /> Seçileni Sil
          </button>
          <button className="btn-ghost text-white hover:text-navy-200"
            onClick={() => setSelectedIds(new Set())}>
            <X size={14} /> İptal
          </button>
        </div>
      )}

      {/* Tablo */}
      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr>
                <th className="th w-10">
                  <input type="checkbox" className="rounded"
                    checked={selectedIds.size === firmalar.length && firmalar.length > 0}
                    onChange={toggleAll} />
                </th>
                {[
                  { key: 'sira', label: '#' },
                  { key: 'firma_adi', label: 'Firma Adı' },
                  { key: 'ilce', label: 'İlçe/OSB' },
                  { key: 'ilgili_kurum', label: 'Kurum' },
                ].map(({ key, label }) => (
                  <th key={key} className="th cursor-pointer hover:bg-navy-100"
                    onClick={() => toggleSort(key)}>
                    <span className="flex items-center gap-1">
                      {label} <SortIcon col={key} />
                    </span>
                  </th>
                ))}
                <th className="th">Mail</th>
                <th className="th">Telefon</th>
                <th className="th w-24">İşlemler</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={8} className="td text-center py-12 text-navy-400">
                    <div className="flex items-center justify-center gap-2">
                      <div className="w-4 h-4 border-2 border-navy-200 border-t-navy-600 rounded-full animate-spin" />
                      Yükleniyor…
                    </div>
                  </td>
                </tr>
              ) : firmalar.length === 0 ? (
                <tr>
                  <td colSpan={8} className="td text-center py-12 text-navy-400">
                    {hasFilter ? 'Filtreyle eşleşen firma bulunamadı' : 'Henüz firma eklenmemiş'}
                  </td>
                </tr>
              ) : firmalar.map((f) => (
                <tr key={f.id} className="table-row">
                  <td className="td">
                    <input type="checkbox" className="rounded"
                      checked={selectedIds.has(f.id)}
                      onChange={() => toggleSelect(f.id)} />
                  </td>
                  <td className="td text-navy-400 font-mono text-xs">{f.sira ?? '—'}</td>
                  <td className="td">
                    <span className="font-medium text-navy-800">{f.firma_adi}</span>
                    {f.notlar && <StickyNote size={11} className="inline-block ml-1.5 text-gold-500" />}
                  </td>
                  <td className="td text-navy-500">{f.ilce || '—'}</td>
                  <td className="td">
                    {f.ilgili_kurum
                      ? <span className="badge bg-navy-100 text-navy-700">{f.ilgili_kurum}</span>
                      : <span className="text-navy-300 text-xs">—</span>
                    }
                  </td>
                  <td className="td">
                    {f.mail
                      ? <a href={`mailto:${f.mail}`} className="text-navy-500 hover:text-navy-800 text-xs truncate block max-w-[180px]">{f.mail}</a>
                      : <span className="badge bg-amber-100 text-amber-700 text-xs">Eksik</span>
                    }
                  </td>
                  <td className="td">
                    {f.telefon
                      ? <span className="text-navy-600 text-sm">{f.telefon}</span>
                      : <span className="badge bg-rose-100 text-rose-600 text-xs">Eksik</span>
                    }
                  </td>
                  <td className="td">
                    <div className="flex items-center gap-1">
                      <button className="btn-ghost p-1.5" onClick={() => navigate(`/firmalar/${f.id}`)}>
                        <Eye size={14} />
                      </button>
                      <button className="btn-ghost p-1.5" onClick={() => { setEditFirma(f); setShowModal(true); }}>
                        <Edit2 size={14} />
                      </button>
                      <button className="btn-ghost p-1.5 hover:text-red-500" onClick={() => setDeleteConfirm(f)}>
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-navy-100">
            <span className="text-xs text-navy-400">
              {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, total)} / {total}
            </span>
            <div className="flex gap-1">
              <button className="btn-ghost py-1 px-2" onClick={() => setPage(1)} disabled={page === 1}>«</button>
              <button className="btn-ghost py-1 px-2" onClick={() => setPage(p => p - 1)} disabled={page === 1}>‹</button>
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                const p = Math.max(1, Math.min(page - 2, totalPages - 4)) + i;
                return (
                  <button key={p}
                    className={`py-1 px-3 rounded-lg text-sm transition-colors ${p === page ? 'bg-navy-600 text-white' : 'btn-ghost'}`}
                    onClick={() => setPage(p)}>{p}
                  </button>
                );
              })}
              <button className="btn-ghost py-1 px-2" onClick={() => setPage(p => p + 1)} disabled={page === totalPages}>›</button>
              <button className="btn-ghost py-1 px-2" onClick={() => setPage(totalPages)} disabled={page === totalPages}>»</button>
            </div>
          </div>
        )}
      </div>

      {/* Modaller */}
      {showModal && (
        <FirmaModal
          firma={editFirma}
          onClose={() => { setShowModal(false); setEditFirma(null); }}
          onSaved={() => { setShowModal(false); setEditFirma(null); load(); }}
        />
      )}
      {showImport && (
        <ImportModal
          onClose={() => setShowImport(false)}
          onImported={() => { load(); }}
        />
      )}

      {/* Silme onayı */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 modal-backdrop bg-navy-900/40">
          <div className="modal-content bg-white rounded-2xl p-6 max-w-sm w-full shadow-2xl">
            <h3 className="font-display text-lg text-navy-800 mb-2">Firmayı Sil</h3>
            <p className="text-navy-500 text-sm mb-5">
              <strong className="text-navy-800">{deleteConfirm.firma_adi}</strong> silinecek. Bu işlem geri alınamaz.
            </p>
            <div className="flex gap-2 justify-end">
              <button className="btn-secondary" onClick={() => setDeleteConfirm(null)}>Vazgeç</button>
              <button className="btn-danger" onClick={() => handleDelete(deleteConfirm.id)}>Evet, Sil</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
